import { Task } from '@/api';
import { Link } from 'react-router';
import { Trans } from 'react-i18next';
import { useFormatDate } from '@/common/hooks';
import { ReactElement, ReactNode, useMemo } from 'react';
import { UIAvatar, UISpacer, UIText, UITooltip } from '@/ui';
import styles from './task-header.module.scss';
import { TaskStatusIndicator } from './task-status-indicator';
import { Battery } from '@/common/components';
import { charcoal, concrete } from '@/ui/styles';
import { useTasksTranslation } from '../hooks';

export type TaskHeaderProps = {
    task: Task;
    simple?: boolean;
    detached?: boolean;
};

export function TaskHeader(props: TaskHeaderProps): ReactElement {
    const { task, simple = true, detached = false } = props;
    const { t } = useTasksTranslation();
    const { formatTimeAndDate } = useFormatDate();

    const startedAt = useMemo(
        () => formatTimeAndDate(new Date(task.startTime)),
        [formatTimeAndDate, task.startTime]
    );

    const endedAt = useMemo(() => {
        if (task.simpleStatus !== 'COMPLETED') return null;
        return task.endTime ? formatTimeAndDate(new Date(task.endTime)) : null;
    }, [formatTimeAndDate, task.endTime, task.simpleStatus]);

    const alertedAt = useMemo(() => {
        if (task.simpleStatus === 'ALERTED' && task.alert?.createdAt) {
            return formatTimeAndDate(new Date(task.alert.createdAt));
        } else {
            return null;
        }
    }, [formatTimeAndDate, task.simpleStatus, task.alert?.createdAt]);

    const containerStyles = [styles.container];
    if (detached) {
        containerStyles.push(styles.detached);
    }

    const battery = useMemo(() => {
        if (!task.battery) return null;
        return {
            level: task.battery.level!,
            isCharging: task.battery.isCharging,
            timestamp: new Date(task.battery.ts)
        };
    }, [task.battery]);

    const signal = useMemo(() => {
        if (!task.signal) return null;
        return {
            type: task.signal.type,
            level: task.signal.level,
            timestamp: new Date(task.signal.ts)
        };
    }, [task.signal]);

    const showIsGuardianPlusEnabled =
        task.simpleStatus !== 'COMPLETED' && task.isArcEnabled;

    return (
        <div className={containerStyles.join(' ')}>
            <div className={styles.leftContainer}>
                <UIAvatar
                    img={task.displayInfo.image}
                    alt={task.displayInfo.name}
                    subAvatar={
                        task.displayInfo.subImage
                            ? {
                                  img: task.displayInfo.subImage,
                                  alt: task.displayInfo.subText ?? ''
                              }
                            : undefined
                    }
                    size={48}
                />
                <div className={styles.textContainer}>
                    <UIText medium>{task.title}</UIText>
                    <UIText>{task.displayInfo.name}</UIText>
                    {simple ? (
                        task.team.deleted ? (
                            <UIText>{t('shared.tasks.deleted_team')}</UIText>
                        ) : (
                            <Link
                                to={`/company/${task.companyId}/settings/teams/${task.team.id}`}
                                style={{
                                    display: 'flex',
                                    gap: 4,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    marginTop: 4,
                                    textDecoration: 'none'
                                }}
                            >
                                <UIAvatar
                                    img={task.team.image ?? ''}
                                    alt={task.team.name ?? ''}
                                    size={18}
                                />
                                <UIText
                                    variant="small"
                                    type="link"
                                    underlineOnHover
                                >
                                    {task.team.name}
                                </UIText>
                            </Link>
                        )
                    ) : null}
                </div>
            </div>
            <div className={styles.rightContainer}>
                {!simple && (
                    <>
                        <RightItem>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center'
                                }}
                            >
                                {task.team.deleted ? (
                                    <UIText>
                                        {t('shared.tasks.deleted_team')}
                                    </UIText>
                                ) : (
                                    <Link
                                        to={`/company/${task.companyId}/settings/teams/${task.team.id}`}
                                        style={{
                                            display: 'flex',
                                            gap: 8,
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            textDecoration: 'none'
                                        }}
                                    >
                                        <UIAvatar
                                            img={task.team.image ?? ''}
                                            alt={task.team.name ?? ''}
                                            size={24}
                                        />
                                        <UIText type="link" underlineOnHover>
                                            {task.team.name}
                                        </UIText>
                                    </Link>
                                )}
                            </div>
                        </RightItem>
                        {showIsGuardianPlusEnabled && (
                            <RightItem>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center'
                                    }}
                                >
                                    <UIText>
                                        {t('shared.tasks.monitoring_24_7')}
                                    </UIText>
                                    <UIText type="link" medium>
                                        {t('shared.labels.enabled')}
                                    </UIText>
                                </div>
                            </RightItem>
                        )}
                        {task.simpleStatus !== 'COMPLETED' &&
                            (battery || signal) && (
                                <RightItem>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 4
                                        }}
                                    >
                                        {signal && (
                                            <TaskSignal signal={signal} />
                                        )}
                                        {battery && (
                                            <Battery battery={battery} />
                                        )}
                                    </div>
                                </RightItem>
                            )}
                        <RightItem>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center'
                                }}
                            >
                                <UIText>
                                    <Trans
                                        t={t}
                                        i18nKey="components.task_header.started_at"
                                        values={{ time: startedAt.time }}
                                        components={{
                                            l: <UIText type="default2" />
                                        }}
                                    />
                                </UIText>
                                <UIText>{startedAt.date}</UIText>
                            </div>
                        </RightItem>
                        {endedAt && (
                            <RightItem>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center'
                                    }}
                                >
                                    <UIText>
                                        <Trans
                                            t={t}
                                            i18nKey="components.task_header.ended_at"
                                            values={{ time: endedAt.time }}
                                            components={{
                                                l: <UIText type="default2" />
                                            }}
                                        />
                                    </UIText>
                                    <UIText>{endedAt.date}</UIText>
                                </div>
                            </RightItem>
                        )}
                        {alertedAt && (
                            <RightItem>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center'
                                    }}
                                >
                                    <UIText>
                                        <Trans
                                            t={t}
                                            i18nKey="components.task_header.alerted_at"
                                            values={{ time: alertedAt.time }}
                                            components={{
                                                l: <UIText type="default2" />
                                            }}
                                        />
                                    </UIText>
                                    <UIText>{alertedAt.date}</UIText>
                                </div>
                            </RightItem>
                        )}
                    </>
                )}

                <div className={styles.rightItem}>
                    <TaskStatusIndicator task={task} filled />
                </div>
            </div>
        </div>
    );
}

function RightItem(props: { children: ReactNode }): ReactElement {
    const { children } = props;
    return (
        <>
            <div className={styles.divider} />
            <div className={styles.rightItem}>{children}</div>
        </>
    );
}

const SIGNAL_HEIGHT = 10;
const SIGNAL_WIDTH = 2;
const signalBarColour = charcoal;
const signalEmptyBarColour = concrete;

type TaskSignalType = '2G' | '3G' | '4G' | '5G' | 'WIFI' | 'UNKNOWN';

type TaskSignalProps = {
    signal: {
        type: TaskSignalType;
        level?: number;
        timestamp: Date;
    } | null;
};

/**
 * The signal-strength bars in the header's device block, beside `Battery`.
 * Nothing else renders it.
 */
export function TaskSignal({ signal }: TaskSignalProps): ReactElement {
    const { t } = useTasksTranslation();
    const { formatSmartShortTimeAndDate } = useFormatDate();

    if (!signal) {
        return (
            <UIText variant={'basic'}>
                {t('components.task_signal.no_data')}
            </UIText>
        );
    }

    // `signal.type` is an API constant that used to reach the screen raw, so
    // users saw `WIFI` and `UNKNOWN`.
    function getTypeText(type: TaskSignalType): string {
        switch (type) {
            case '2G':
                return t('shared.tasks.signal_type.2g');
            case '3G':
                return t('shared.tasks.signal_type.3g');
            case '4G':
                return t('shared.tasks.signal_type.4g');
            case '5G':
                return t('shared.tasks.signal_type.5g');
            case 'WIFI':
                return t('shared.tasks.signal_type.wifi');
            default:
                return t('shared.tasks.signal_type.unknown');
        }
    }

    // ALL CAPS carried in the string rather than upper-cased in code — Spanish
    // does not capitalise the same way.
    const level = signal.level;
    let strengthText: string | null = '';
    if (level === undefined) {
        strengthText = null;
    } else {
        if (level <= 0) {
            strengthText = t('components.task_signal.strength_none');
        } else if (level === 1) {
            strengthText = t('components.task_signal.strength_poor');
        } else if (level === 2) {
            strengthText = t('components.task_signal.strength_fair');
        } else if (level === 3) {
            strengthText = t('components.task_signal.strength_good');
        } else {
            strengthText = t('components.task_signal.strength_excellent');
        }
    }

    const typeText = getTypeText(signal.type);

    const tooltip = t('components.task_signal.tooltip', {
        type: typeText,
        strength: strengthText
            ? strengthText
            : t('shared.labels.not_available'),
        time: formatSmartShortTimeAndDate(signal.timestamp)
    });

    return (
        <UITooltip text={tooltip}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-end',
                        height: SIGNAL_HEIGHT
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            height: SIGNAL_HEIGHT / 4,
                            width: SIGNAL_WIDTH,
                            backgroundColor: signal.level
                                ? signal.level >= 1
                                    ? signalBarColour
                                    : signalEmptyBarColour
                                : signalEmptyBarColour
                        }}
                    />
                    <div
                        style={{
                            display: 'flex',
                            marginLeft: SIGNAL_WIDTH / 2,
                            height: SIGNAL_HEIGHT / 2,
                            width: SIGNAL_WIDTH,
                            backgroundColor: signal.level
                                ? signal.level >= 2
                                    ? signalBarColour
                                    : signalEmptyBarColour
                                : signalEmptyBarColour
                        }}
                    />
                    <div
                        style={{
                            display: 'flex',
                            marginLeft: SIGNAL_WIDTH / 2,
                            height: (SIGNAL_HEIGHT / 4) * 3,
                            width: SIGNAL_WIDTH,
                            backgroundColor: signal.level
                                ? signal.level >= 3
                                    ? signalBarColour
                                    : signalEmptyBarColour
                                : signalEmptyBarColour
                        }}
                    />
                    <div
                        style={{
                            display: 'flex',
                            marginLeft: SIGNAL_WIDTH / 2,
                            height: SIGNAL_HEIGHT,
                            width: SIGNAL_WIDTH,
                            backgroundColor: signal.level
                                ? signal.level >= 4
                                    ? signalBarColour
                                    : signalEmptyBarColour
                                : signalEmptyBarColour
                        }}
                    />
                </div>
                <UISpacer multiplier={0.5} />
                <UIText variant={'basic'}>{typeText}</UIText>
            </div>
        </UITooltip>
    );
}
